import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { GetViewHallBookingById } from "adminApi/HallBookingApi/HallBooking";
import Spinner from "Common/Globle/Spinner";
import PageBody from 'Common/Custom/PageBody'


// Helper components for clean structure
const DetailSubCard = ({ title, children }) => (
    <div className="col-12">
        <div className="card shadow-sm border h-100" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-light border-bottom text-muted fw-bold py-3" style={{ borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                {title}
            </div>
            <div className="card-body p-4">
                {children}
            </div>
        </div>
    </div>
);

const Field = ({ label, value, isFullWidth = false, badge = null, isBold = false, colClass = "col-md-6 col-lg-3" }) => (
    <div className={isFullWidth ? "col-12" : colClass}>
        <div className="mb-2">
            <label className="text-muted fw-bold mb-1" style={{ fontSize: '11px', textTransform: 'uppercase' }}>{label}</label>
            <div>
                {badge ? (
                    <span className={`badge bg-${badge} px-2 py-1 rounded`}>{value}</span>
                ) : (
                    <span className={`text-dark ${isBold ? 'fw-bold fs-5' : 'fw-medium fs-6'}`}>{value || "-"}</span>
                )}
            </div>
        </div>
    </div>
);

const ViewHallBooking = () => {
    const { id } = useParams();
    const [loadingFetch, setLoadingFetch] = useState(false);
    const [booking, setBooking] = useState(null);

    const formatDateTime = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleString('en-IN');
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-IN');
    };

    const formatCurrency = (value) => {
        const num = Number(value) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed': return 'success';
            case 'pending': return 'warning';
            case 'cancelled': return 'danger';
            case 'completed': return 'info';
            default: return 'secondary';
        }
    };

    useEffect(() => {
        const feachDataToFill = async () => {
            setLoadingFetch(true);
            try {
                const res = await GetViewHallBookingById(id);
                if (res?.data?.success && res.data.data) {
                    setBooking(res.data.data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoadingFetch(false);
            }
        };

        if (id) feachDataToFill();
    }, [id]);

    if (loadingFetch) return <Spinner />;
    if (!booking) return <div className="text-center mt-5 text-muted">Booking not found.</div>;

    const halls = booking.halls || [];

    return (
        <PageBody>

            {/* Page Header */}
            <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <div>
                        <h3 className="fw-bold text-dark mb-1">View Hall Booking</h3>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item"><Link to="/index" className="text-decoration-none">Admin</Link></li>
                                <li className="breadcrumb-item"><Link to="/event-booking/hall-booking" className="text-decoration-none">Hall Booking</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">View</li>
                            </ol>
                        </nav>
                    </div>
                    <div>
                        <Link to={`/Hallbookinglist/edit-hall-booking/${id}`} className="btn btn-primary px-4 py-2 shadow-sm rounded-pill fw-medium">
                            <i className="fa fa-pencil me-2"></i> Edit Booking
                        </Link>
                    </div>
                </div>
            </div>

            {/* First Row: Event and Customer Details */}
            <div className="row g-4 mb-4">
                {/* Event Details */}
                <div className="col-lg-6">
                    <DetailSubCard title="Event Details">
                        <div className="row g-4">
                            <Field label="Booking Number" value={booking.booking_number} colClass="col-md-6" />
                            <Field label="Event Name" value={booking.event_name} colClass="col-md-6" />
                            <Field label="Event Type" value={booking.event_type} colClass="col-md-6" />
                            <Field
                                label="Booking Status"
                                value={booking.booking_status || "Pending"}
                                badge={getStatusColor(booking.booking_status)}
                                colClass="col-md-6"
                            />
                            <Field label="Start Date" value={formatDateTime(booking.start_date)} colClass="col-md-6" />
                            <Field label="End Date" value={formatDateTime(booking.end_date)} colClass="col-md-6" />
                            <Field label="Remarks" value={booking.remarks || "No remarks provided."} isFullWidth />
                        </div>
                    </DetailSubCard>
                </div>

                {/* Customer Details */}
                <div className="col-lg-6">
                    <DetailSubCard title="Customer Details">
                        <div className="row g-4">
                            <Field label="Customer Name" value={booking.customer_name} colClass="col-md-4" />
                            <Field label="Phone Number" value={booking.customer_phone} colClass="col-md-4" />
                            <Field label="Email Address" value={booking.customer_email} colClass="col-md-4" />

                            <Field label="Gender" value={booking.gender} colClass="col-md-4" />
                            <Field label="Date of Birth" value={formatDate(booking.dob)} colClass="col-md-4" />
                            <Field label="Anniversary" value={formatDate(booking.anniversary)} colClass="col-md-4" />

                            <Field label="Address" value={booking.address} colClass="col-md-4" />

                            <Field label="City" value={booking.city} colClass="col-md-4" />
                            <Field label="Country" value={booking.country} colClass="col-md-4" />
                            <Field label="Zip Code" value={booking.zip_code} colClass="col-md-4" />

                            <Field label="Nationality" value={booking.nationality} colClass="col-md-4" />
                            <Field label="Profession" value={booking.profession} colClass="col-md-4" />

                            <Field label="PID Type" value={booking.pid_type} colClass="col-md-4" />
                            <Field label="PID No" value={booking.pid} colClass="col-md-4" />
                        </div>
                    </DetailSubCard>
                </div>
            </div>

            {/* Hall & Food Details */}
            <DetailSubCard title="Hall & Food Details">
                {halls.length === 0 ? (
                    <div className="text-muted text-center py-3">No halls assigned to this booking.</div>
                ) : (
                    <div className="row g-4">
                        {halls.map((hall, idx) => {
                            // Group foods by food_type_name
                            const groupedFoods = {};
                            if (hall.foods) {
                                hall.foods.forEach(f => {
                                    if (!groupedFoods[f.food_type_name]) groupedFoods[f.food_type_name] = [];
                                    groupedFoods[f.food_type_name].push(f.food_name);
                                });
                            }

                            return (
                                <div className="col-12" key={hall.hall_booking_detail_id || idx}>
                                    <div className="card shadow-sm border border-secondary border-opacity-25" style={{ borderRadius: '8px' }}>
                                        {/* Hall Header */}
                                        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-primary fs-5">
                                                {hall.hallroom_type || "Unknown Hall"}
                                            </span>
                                            <span className="badge bg-secondary px-3 py-2 rounded-pill">
                                                Hall {idx + 1}
                                            </span>
                                        </div>

                                        <div className="card-body p-4">
                                            {/* Hall Info Grid */}
                                            <div className="row g-4 mb-4 pb-4 border-bottom">
                                                <Field label="Hall Room No" value={hall.hall_room_no || "N/A"} colClass="col-md-4" />
                                                <Field label="Seat Plan" value={hall.seatplan_name} colClass="col-md-4" />
                                                <Field label="Capacity" value={`${hall.capacity || "0"} People`} colClass="col-md-4" />
                                                <Field label="Hourly Rent" value={`₹${formatCurrency(hall.hourly_rate)}`} isBold colClass="col-md-6" />
                                                <Field
                                                    label="Include Hall Rent"
                                                    value={hall.include_hall_rent ? "Yes" : "No"}
                                                    badge={hall.include_hall_rent ? "primary" : "secondary"}
                                                    colClass="col-md-6"
                                                />
                                            </div>

                                            {/* Food Package Section */}
                                            {hall.package_name && (
                                                <div className="mt-4">
                                                    <h6 className="fw-bold text-dark mb-3">Food & Beverage Package</h6>
                                                    <div className="row g-4 p-4 rounded mb-4" style={{ backgroundColor: "#f8f9fa" }}>
                                                        <Field label="Package Name" value={hall.package_name} colClass="col-md-4" />
                                                        <Field label="Price per person" value={`₹${formatCurrency(hall.food_price)}`} colClass="col-md-4" />
                                                        <Field label="Quantity" value={hall.food_quantity || 0} colClass="col-md-4" />
                                                    </div>

                                                    {/* Menu Items */}
                                                    <div>
                                                        {Object.keys(groupedFoods).length > 0 && (
                                                            <div>
                                                                <label className="form-label text-muted fw-bold mb-3" style={{ fontSize: '0.875rem' }}>Selected Menu Items:</label>
                                                                <div className="d-flex flex-column gap-3">
                                                                    {Object.entries(groupedFoods).map(([type, items]) => (
                                                                        <div key={type} className="d-flex flex-column mb-3">
                                                                            <span className="text-secondary fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                                                                                {type}
                                                                            </span>
                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                {items.map((item, i) => (
                                                                                    <span key={i} className="bg-white text-dark border px-3 py-1 rounded-pill" style={{ fontSize: '0.9rem' }}>
                                                                                        {item}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </DetailSubCard>

            {/* Billing Summary */}
            <DetailSubCard title="Billing Summary">
                <div className="row">
                    {/* Payment Details */}
                    <div className="col-lg-6 border-end pe-4 mb-4 mb-lg-0">
                        <h6 className="fw-bold text-muted mb-4 border-bottom pb-2">Payment Details</h6>
                        <div className="row g-4">
                            <Field label="Payment Method" value={booking.payment_method_name || "-"} isFullWidth />
                            <Field label="Payment Remarks" value={booking.payment_remarks || "No remarks provided."} isFullWidth />
                        </div>
                    </div>

                    {/* Cost Calculation */}
                    <div className="col-lg-6 ps-lg-4">
                        <div className="d-flex flex-column h-100">
                            <div className="d-flex justify-content-between mb-3 text-muted fw-medium">
                                <span>Total Hall Rent</span>
                                <span className="text-dark">₹{formatCurrency(booking.booking_charge || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 text-muted fw-medium border-bottom pb-3">
                                <span>Total Food Charge</span>
                                <span className="text-dark">₹{formatCurrency(booking.food_charge || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pt-2">
                                <span className="fw-bold text-dark fs-5">Total Amount</span>
                                <span className="fw-bold text-dark fs-5">₹{formatCurrency(booking.total_amount || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-4">
                                <span className="fw-medium text-muted">Paid Amount</span>
                                <span className="fw-bold text-success">₹{formatCurrency(booking.paid_amount || 0)}</span>
                            </div>

                            {/* Due Amount Highlight */}
                            <div className="mt-auto p-3 rounded" style={{ backgroundColor: "#fff5f5", border: "1px dashed #ffa8a8" }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold text-danger fs-5">Due Amount</span>
                                    <span className="fw-bold text-danger fs-4">₹{formatCurrency(booking.due_amount || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DetailSubCard>

        </PageBody>
    );
};

export default ViewHallBooking;
